/**
 * apps/web :: components/paywall/TrialExpiredPaywall
 *
 * Renders the post-trial paywall body that takes over `<DashboardPage>`
 * when `useEntitlement().isExpired === true`. Distinct from
 * `<Paywall>` (which is the surface-route gate's full tier-picker) —
 * this is a focused "your trial ended; here's what unlocking buys you"
 * card with state-anchored value props and a single CTA.
 *
 * Language toggle:
 *   The user picks during onboarding which native language they want
 *   their study materials in. When the trial ends and they land on
 *   this paywall, the very first conversion friction is "I want to
 *   read this in my own language before I pay $20." So we show two
 *   buttons in the top-right of the card: "English" and the native
 *   label of their `nativeLanguage` (e.g. "Español" for ES, "हिन्दी"
 *   for HI, "Türkçe" for TR). Toggling swaps the body copy.
 *
 * TRANSLATION-REVIEW-PENDING: the non-English bundles below are
 *   STUBBED to the EN strings as placeholders. Shipping those English
 *   strings under a non-English locale label would be content-correctness
 *   fraud — content-ops owns the populate-the-bundle ticket. Reviewers
 *   should reject any PR that flips these stubs to "translated" copy
 *   without an attested native-speaker review (Translation-Review-Required
 *   label is the gate; see CONVENTIONS.md §"The Four Non-Negotiables").
 *
 * Routing:
 *   The CTA points at `/pricing`. The marketing site at /pricing handles
 *   tier selection, currency display, and Stripe Checkout redirection.
 *   We use a plain anchor (not <Link>) because /pricing is a marketing
 *   route hosted at the .org / app entry point, not an internal
 *   `react-router` segment.
 *
 * Failure-mode contract:
 *   - When `nativeLanguage` is missing, "en", or any code that is not in
 *     `LOCALE_DEFINITIONS`, we render English-only and hide the toggle.
 *     Defensive default: better to ship an English paywall than crash on
 *     a future locale code we don't yet know about.
 */
import { useState } from "react";

import { LOCALE_DEFINITIONS } from "@/i18n/locales";

export interface TrialExpiredPaywallProps {
  /**
   * The user's `nativeLanguage` from `GET /api/profile` (e.g. "es", "hi",
   * "zh-Hans"). Drives the second toggle button label and the body copy
   * when toggled.
   */
  nativeLanguage?: string | null;
}

/**
 * Locale codes the paywall has copy for. Currently every locale ships
 * the EN strings under TRANSLATION-REVIEW-PENDING; flipping a locale
 * to true real translation requires Translation-Review-Required gate.
 */
type PaywallLocale = "en" | "es" | "hi" | "zh-CN" | "tl" | "tr";

interface PaywallCopy {
  headline: string;
  subhead: string;
  bullets: readonly string[];
  cta: string;
  priceNote: string;
  seeIncluded: string;
}

/**
 * Single source of truth for the paywall's translated copy.
 *
 * EN: production-ready.
 *
 * TRANSLATION-REVIEW-PENDING: every other locale is the EN copy as a
 * placeholder. Content-ops will populate these per `Translation-Review-
 * Required` review queue entries, one locale per PR, with native-speaker
 * sign-off attached to the ticket.
 *
 * The third bullet uses `{{language}}` interpolation for the native
 * language name; rendered through a tiny replace() rather than i18next
 * because this component is intentionally self-contained for the
 * language-toggle UX (i18next's active language is the chrome's
 * language; the toggle here drives a different axis).
 */
const PAYWALL_COPY: Record<PaywallLocale, PaywallCopy> = {
  en: {
    headline: "Your 24-hour trial has ended.",
    subhead:
      "Your rule-diff, your progress, and your practice questions are still here.",
    bullets: [
      "The rules that are actually new to you — not the full manual. Based on where you learned and which state you're testing. Most drivers: 10–20 rules.",
      "Practice that matches your state's exact exam — same question count, pass threshold, and time limit. Spaced repetition on your misses.",
      "Everything in {{language}} — manual, rule-diff, practice tests. No switching apps.",
    ],
    cta: "Unlock full access →",
    priceNote: "From $20 · per state · 6 months · no subscription",
    seeIncluded: "See what's included →",
  },
  // Translation-Review-Required: machine-translated draft; native-speaker
  // sign-off required before shipping to production users.
  es: {
    headline: "Tu período de prueba de 24 horas ha terminado.",
    subhead:
      "Tu análisis de reglas, tu progreso y tus preguntas de práctica siguen aquí.",
    bullets: [
      "Las reglas que son realmente nuevas para ti — no el manual completo. Basado en dónde aprendiste a manejar y en qué estado te vas a examinar. La mayoría de conductores: 10–20 reglas.",
      "Práctica que coincide con el examen exacto de tu estado — misma cantidad de preguntas, umbral de aprobación y límite de tiempo. Repetición espaciada en tus respuestas incorrectas.",
      "Todo en {{language}} — manual, análisis de reglas, pruebas de práctica. Sin cambiar de aplicación.",
    ],
    cta: "Desbloquear acceso completo →",
    priceNote: "Desde $20 · por estado · 6 meses · sin suscripción",
    seeIncluded: "Ver qué está incluido →",
  },
  // Translation-Review-Required: machine-translated draft; native-speaker sign-off required.
  hi: {
    headline: "आपका 24 घंटे का ट्रायल समाप्त हो गया है।",
    subhead:
      "आपका रूल-डिफ़, आपकी प्रगति और आपके अभ्यास प्रश्न अभी भी यहाँ हैं।",
    bullets: [
      "वे नियम जो वास्तव में आपके लिए नए हैं — पूरा मैनुअल नहीं। इस पर आधारित कि आपने कहाँ गाड़ी चलानी सीखी और आप किस राज्य में परीक्षा दे रहे हैं। अधिकांश ड्राइवर: 10–20 नियम।",
      "अभ्यास जो आपके राज्य की सटीक परीक्षा से मेल खाता है — समान प्रश्न संख्या, पास थ्रेशोल्ड और समय सीमा। आपकी गलतियों पर स्पेस्ड रिपीटिशन।",
      "{{language}} में सब कुछ — मैनुअल, रूल-डिफ़, अभ्यास परीक्षण। ऐप बदलने की ज़रूरत नहीं।",
    ],
    cta: "पूर्ण एक्सेस अनलॉक करें →",
    priceNote: "$20 से शुरू · प्रति राज्य · 6 महीने · कोई सब्सक्रिप्शन नहीं",
    seeIncluded: "क्या शामिल है देखें →",
  },
  // Translation-Review-Required: machine-translated draft; native-speaker sign-off required.
  "zh-CN": {
    headline: "您的 24 小时试用已结束。",
    subhead:
      "您的规则对比、学习进度和练习题仍在这里。",
    bullets: [
      "真正对您来说是新内容的规则——不是完整手册。根据您在哪里学车以及在哪个州参加考试而定。大多数驾驶员：10–20 条规则。",
      "与您所在州的真实考试完全匹配的练习——相同的题目数量、及格分数和时间限制。针对您答错题目的间隔重复练习。",
      "一切内容均以 {{language}} 呈现——手册、规则对比、练习测试。无需切换应用。",
    ],
    cta: "解锁完整访问权限 →",
    priceNote: "起价 $20 · 每个州 · 6 个月 · 无订阅",
    seeIncluded: "查看包含内容 →",
  },
  // Translation-Review-Required: machine-translated draft; native-speaker sign-off required.
  tl: {
    headline: "Natapos na ang iyong 24-oras na pagsubok.",
    subhead:
      "Ang iyong rule-diff, pag-unlad, at mga tanong sa pagsasanay ay nandito pa rin.",
    bullets: [
      "Ang mga panuntunang bago talaga sa iyo — hindi ang buong manwal. Batay sa kung saan ka natuto at kung saang estado ka nagtatake ng eksamen. Karamihang mga driver: 10–20 panuntunan.",
      "Pagsasanay na tugma sa eksaktong eksamen ng iyong estado — parehong bilang ng tanong, threshold ng pagpasa, at limitasyon sa oras. Spaced repetition sa iyong mga pagkakamali.",
      "Lahat sa {{language}} — manwal, rule-diff, mga practice test. Hindi na kailangang mag-switch ng app.",
    ],
    cta: "I-unlock ang buong access →",
    priceNote: "Mula $20 · bawat estado · 6 na buwan · walang subscription",
    seeIncluded: "Tingnan ang kasama →",
  },
  // Translation-Review-Required: machine-translated draft; native-speaker sign-off required.
  tr: {
    headline: "24 saatlik deneme süreniz sona erdi.",
    subhead:
      "Kural karşılaştırmanız, ilerlemeniz ve pratik sorularınız hâlâ burada.",
    bullets: [
      "Gerçekten sizin için yeni olan kurallar — tam el kitabı değil. Nerede öğrendiğinize ve hangi eyalette sınava girdiğinize göre. Çoğu sürücü: 10–20 kural.",
      "Eyaletinizin tam sınavıyla eşleşen pratik — aynı soru sayısı, geçme eşiği ve süre sınırı. Hatalarınız üzerinde aralıklı tekrar.",
      "{{language}} dilinde her şey — el kitabı, kural karşılaştırması, pratik testler. Uygulama değiştirmeye gerek yok.",
    ],
    cta: "Tam erişimi aç →",
    priceNote: "$20'den itibaren · eyalet başına · 6 ay · abonelik yok",
    seeIncluded: "Nelerin dahil olduğunu gör →",
  },
};

/**
 * The display name for a locale, in its OWN script. Sourced from
 * `LOCALE_DEFINITIONS` so any new locale we ship picks up automatically.
 *
 * Returns `null` for codes we don't recognise — the caller renders
 * English-only when this is null.
 */
function nativeLabelFor(code: string): string | null {
  const def = LOCALE_DEFINITIONS.find((l) => l.code === code);
  return def?.nativeLabel ?? null;
}

/**
 * Map a `nativeLanguage` profile code (which may be `zh-Hans`,
 * `te`, `ru`, etc.) onto a `PAYWALL_COPY` key. Backend supports a wider
 * `LangCode` union than the chrome ships UI translations for, so we
 * normalise via:
 *   - direct match if the code is a paywall-locale key.
 *   - `zh-Hans` → `zh-CN` (the same script).
 *   - everything else returns null so the caller falls back to EN-only.
 *
 * Codes covered today: en, es, hi, zh-CN/zh-Hans, tl, tr.
 */
function paywallLocaleFor(code: string | null | undefined): PaywallLocale | null {
  if (!code) return null;
  if (code === "en") return "en";
  if (code === "es") return "es";
  if (code === "hi") return "hi";
  if (code === "zh-CN" || code === "zh-Hans") return "zh-CN";
  if (code === "tl") return "tl";
  if (code === "tr") return "tr";
  return null;
}

export default function TrialExpiredPaywall({
  nativeLanguage,
}: TrialExpiredPaywallProps): JSX.Element {
  // Resolve the user's native paywall locale. If the code is unsupported
  // (or already English), the toggle is hidden and the body always shows EN.
  const nativeLocale = paywallLocaleFor(nativeLanguage ?? null);
  const showToggle = nativeLocale !== null && nativeLocale !== "en";
  const nativeLabel = showToggle ? nativeLabelFor(nativeLanguage as string) : null;

  // Active locale defaults to EN. Toggling flips between EN and the
  // user's native locale (when one exists).
  const [activeLocale, setActiveLocale] = useState<PaywallLocale>("en");
  const copy = PAYWALL_COPY[activeLocale];

  // Render the native-language name into bullet 3 — for EN copy this is
  // the user's nativeLabel (e.g. "Español"); for the toggled-native view
  // this is the same label rendered in its own script. When no native
  // locale is detected we fall back to "your language" for safety.
  const interpolatedLanguage =
    nativeLabel ?? nativeLabelFor("en") ?? "your language";
  const bullets = copy.bullets.map((b) =>
    b.replace("{{language}}", interpolatedLanguage),
  );

  return (
    <section
      data-testid="trial-expired-paywall"
      data-active-locale={activeLocale}
      aria-labelledby="trial-expired-paywall-headline"
      className="mx-auto mt-xl max-w-2xl rounded-lg border border-brand-primary/10 bg-white p-lg shadow-sm sm:p-xl"
    >
      <header className="flex flex-wrap items-start justify-between gap-md">
        <div className="min-w-0 flex-1">
          <h1
            id="trial-expired-paywall-headline"
            className="font-display text-2xl font-bold tracking-tight text-brand-primary sm:text-3xl"
          >
            {copy.headline}
          </h1>
          <p className="mt-sm text-base text-neutral-800">{copy.subhead}</p>
        </div>
        {showToggle && (
          <div
            data-testid="trial-expired-paywall-lang-toggle"
            role="group"
            aria-label="Paywall language"
            className="inline-flex shrink-0 rounded-md border border-brand-primary/20 bg-brand-surface/40 p-xxs"
          >
            <button
              type="button"
              onClick={() => setActiveLocale("en")}
              aria-pressed={activeLocale === "en"}
              className={`rounded px-sm py-xxs text-xs font-semibold transition-colors min-h-[44px] ${
                activeLocale === "en"
                  ? "bg-brand-primary text-white"
                  : "text-brand-primary hover:bg-white"
              }`}
            >
              English
            </button>
            <button
              type="button"
              onClick={() => setActiveLocale(nativeLocale as PaywallLocale)}
              aria-pressed={activeLocale !== "en"}
              lang={nativeLocale ?? undefined}
              className={`rounded px-sm py-xxs text-xs font-semibold transition-colors min-h-[44px] ${
                activeLocale !== "en"
                  ? "bg-brand-primary text-white"
                  : "text-brand-primary hover:bg-white"
              }`}
            >
              {nativeLabel}
            </button>
          </div>
        )}
      </header>

      <ul
        data-testid="trial-expired-paywall-bullets"
        className="mt-lg space-y-md"
      >
        {bullets.map((b, i) => (
          <li key={i} className="flex gap-sm text-sm text-neutral-800">
            <span
              aria-hidden="true"
              className="mt-xxs inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-primary/10 text-xs font-bold text-brand-primary"
            >
              {i + 1}
            </span>
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="mt-lg flex flex-wrap items-center justify-between gap-md">
        <a
          data-testid="trial-expired-paywall-cta"
          href="/pricing"
          className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white no-underline transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {copy.cta}
        </a>
        <p
          data-testid="trial-expired-paywall-price-note"
          className="text-xs text-neutral-700"
        >
          {copy.priceNote}
        </p>
      </div>

      <p className="mt-md">
        <a
          data-testid="trial-expired-paywall-see-included"
          href="/pricing"
          className="text-sm text-brand-primary underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {copy.seeIncluded}
        </a>
      </p>
    </section>
  );
}
